import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getSupabaseClient } from "../../../lib/supabaseClient";
import { usePhotographerAuth } from "../../../contexts/PhotographerAuthContext";

export default function PhotographerLoginPage() {
  const { session, login } = usePhotographerAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session) navigate("/photographer/dashboard", { replace: true });
  }, [session, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const supabase = getSupabaseClient();
      const { data, error: dbError } = await supabase
        .from("photographers")
        .select("id,display_name,email,photographer_code")
        .eq("email", email.trim().toLowerCase())
        .eq("is_active", true)
        .maybeSingle();

      if (dbError) throw dbError;
      if (!data) {
        setError("找不到此 Email 的攝影師帳號，請確認後重試。");
        return;
      }
      login({ id: data.id, display_name: data.display_name, email: data.email, photographer_code: data.photographer_code ?? "" });
      navigate("/photographer/dashboard", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "連線失敗，請稍後重試。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#C8B8A6]/20 text-[#C8B8A6] mb-4">
            <i className="ri-camera-2-line text-xl" />
          </div>
          <h1 className="text-lg font-light tracking-widest uppercase text-[#2C2C2C] mb-1">TaBiFoTo</h1>
          <p className="text-xs font-light text-[#2C2C2C]/40 tracking-wide">攝影師入口</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl border border-[#D9D9D9]/60 p-8">
          <h2 className="text-base font-light text-[#2C2C2C] tracking-wide mb-1">攝影師登入</h2>
          <p className="text-xs font-light text-[#2C2C2C]/40 mb-6">輸入您的 Email 以存取拍攝指派</p>

          <form onSubmit={handleLogin} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-light text-[#2C2C2C]/60 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full border border-[#D9D9D9] rounded-lg px-3 py-2.5 text-sm font-light focus:outline-none focus:border-[#C8B8A6] placeholder:text-[#2C2C2C]/20"
              />
            </div>

            {error && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200/60 rounded-lg px-3 py-2.5">
                <div className="w-4 h-4 flex items-center justify-center text-red-400 mt-0.5 shrink-0">
                  <i className="ri-error-warning-line text-sm" />
                </div>
                <p className="text-xs font-light text-red-600">{error}</p>
              </div>
            )}

            <button type="submit" disabled={loading || !email.trim()}
              className="w-full py-2.5 bg-[#2C2C2C] text-white text-sm font-light rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#3C3C3C] transition-colors disabled:opacity-40 mt-1">
              {loading ? "確認中..." : "登入"}
            </button>
          </form>
        </div>

        <p className="text-center text-xs font-light text-[#2C2C2C]/30 mt-6">
          如需協助，請聯絡 TaBiFoTo 運營團隊
        </p>
      </div>
    </div>
  );
}
